import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  SIDEBAR_STORAGE_KEY,
  SidebarInset,
  SidebarProvider,
  SidebarTrigger,
  useSidebar,
} from "@/components/ui/sidebar";
import { local } from "@/lib/storage";
import { BrandMark } from "../brand/BrandMark.tsx";
import { clearSpace, LOCKUP_MIN_WIDTH } from "../brand/EsiMark.tsx";
import { HOME_PATH, Link, type Route } from "../router.tsx";
import { IdpRefresh } from "./IdpRefresh.tsx";
import { MAIN_ID } from "./main-region.ts";
import { NotificationBell } from "./NotificationBell.tsx";
import { SessionEnded } from "./SessionEnded.tsx";
import { AppSidebar } from "./sidebar/AppSidebar.tsx";

const rememberedOpen = () => local.get(SIDEBAR_STORAGE_KEY) !== "collapsed";

/**
 * The product frame: a skip link, the collapsible sidebar (a sheet below `md`) and the main
 * region. Tab order: skip link, sidebar, main content.
 */
export function AppShell({ route, children }: { route: Route; children: ReactNode }) {
  const { t } = useTranslation();
  return (
    <>
      <a
        href={`#${MAIN_ID}`}
        onClick={(e) => {
          // Move focus, not only the scroll position, so the next Tab continues in the content.
          e.preventDefault();
          document.getElementById(MAIN_ID)?.focus();
        }}
        className="sr-only z-50 rounded-md bg-surface-overlay px-3 py-2 text-sm font-medium text-fg shadow-md focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
      >
        {t("app.skipToContent")}
      </a>
      {/* A viewport frame: the document never scrolls; each screen owns its scroll area. */}
      <SidebarProvider defaultOpen={rememberedOpen()} className="h-dvh min-h-0 overflow-hidden">
        <AppSidebar route={route} mark={<HomeMark clear="end" tenantClassName="h-6 max-w-40" />} />
        <SidebarInset
          data-esi-content="page"
          id={MAIN_ID}
          tabIndex={-1}
          className="min-h-0 min-w-0 overflow-hidden outline-none"
        >
          <MobileBar />
          <SessionEnded />
          <IdpRefresh />
          {children}
        </SidebarInset>
      </SidebarProvider>
    </>
  );
}

/** Below `md` the sidebar is a sheet: this bar opens it. */
function MobileBar() {
  const { t } = useTranslation();
  const { isMobile } = useSidebar();
  if (!isMobile) {
    return null;
  }
  return (
    <div className="flex h-12 shrink-0 items-center gap-2 border-b border-line bg-surface px-3">
      <SidebarTrigger aria-label={t("nav.openMenu")} />
      <HomeMark clear="start" tenantClassName="h-6 max-w-32" />
      <NotificationBell className="ml-auto size-9" />
    </div>
  );
}

/**
 * The product mark, linked home: the ESI lockup at its 96 px minimum (named "ESI"), or the
 * tenant's logo in its place. Clear space holds on the side facing a control; the 48 px bar's
 * own edges are ground (decision D2).
 */
function HomeMark({ clear, tenantClassName }: { clear: "start" | "end"; tenantClassName: string }) {
  return (
    <Link href={HOME_PATH} className="block rounded-sm" style={clearSpace(LOCKUP_MIN_WIDTH, clear)}>
      <BrandMark width={LOCKUP_MIN_WIDTH} tenantClassName={tenantClassName} />
    </Link>
  );
}
